import React, { useRef, useState } from "react";
import {
  ReclaimProofRequest,
  transformForOnchain,
} from "@reclaimprotocol/js-sdk/dist";
import {
  RECLAIM_APPS,
  RECLAIM_BANKS,
} from "@/constants/reclaim-protocol.constant";
import { useToast } from "./ui/use-toast";
import { useContractWrite } from "@/hooks";
import { Button } from "./ui/button/button";
import { ButtonSize, ButtonType } from "./ui/button/button.type";
import { orderProcessorContract, useSmartWallet } from "@/contexts/SmartWallet";
import { Input } from "./ui/input/input";
import { useSendTransaction } from "thirdweb/react";
import { OrderType } from "@/models";
import { Bottomsheet } from "./bottomsheet";
import { BankSelector } from "./BankSelector";
import Axios from "axios";
import { customPrepareContractCall } from "@/helper/thirdweb";
import { trackAmplitudeEvent } from "@/utils/amplitude";

type DisputeButtonTypes = {
  transId: string;
  label: string;
  orderId?: string;
  onlyCta?: boolean;
  orderType: OrderType.SELL;
  successCallback: () => void;
  redactedAccNum: number;
  amount?: string;
  merchantBank: string | null;
};

const DisputeButton: React.FC<DisputeButtonTypes> = ({
  transId,
  label,
  orderId,
  onlyCta = false,
  orderType,
  successCallback,
  redactedAccNum,
  amount,
  merchantBank,
}) => {
  const { toast } = useToast();
  const bankSelectorRef = useRef<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [transIdValue, setTransIdValue] = useState<number | null>(null);
  const { account } = useSmartWallet();

  const { mutateAsync: mutateSettleDispute } = useContractWrite();

  const { mutate: sendSetDisputeTransIdTransaction, isPending } =
    useSendTransaction();

  const handleSetDisputeTransId = async () => {
    try {
      trackAmplitudeEvent("DISPUTE_TRANSACTION_ID_SET");
      const transaction = await customPrepareContractCall({
        contract: orderProcessorContract,
        method: "setDisputeTransId",
        params: [orderId, transIdValue],
        address: account?.address,
      });
      sendSetDisputeTransIdTransaction(transaction as any, {
        onSuccess: (data) => {
          toast({
            title: "TransId Updated Successfully",
          });
          console.log(
            "Transaction sendSetDisputeTransIdTransaction confirmed successfully",
            data,
          );
          transIdValue && openReclaimView(transIdValue?.toString());
        },
        onError: (error) => {
          toast({
            title: `Error: ${error.message}`,
            variant: "destructive",
          });
          console.error(error);
        },
      });
    } catch (err) {
      toast({
        title: `Something went wrong, please try again!`,
        variant: "destructive",
      });
    }
  };

  const openReclaimView = async (trans_id: string) => {
    try {
      trackAmplitudeEvent("DISPUTE_RECLAIM_STARTED");
      let reclaimProtocol = null;
      let params: {
        trans_id: string;
        account_number: string;
        credit_amount?: string;
      } = {
        trans_id,
        account_number: `${redactedAccNum}`,
      };
      if (!merchantBank) return;
      // @ts-ignore
      reclaimProtocol = RECLAIM_APPS[merchantBank.toUpperCase()];

      console.log(reclaimProtocol);
      if (orderType == OrderType.SELL) {
        reclaimProtocol = reclaimProtocol.DBG_TRANS;
        if (merchantBank == RECLAIM_BANKS.CANARA && amount) {
          params = {
            ...params,
            credit_amount: amount,
          };
        }
      } else {
        //sbi no transaction
        reclaimProtocol = reclaimProtocol.NO_TRANS;
        params = {
          ...params,
        };
      }
      setIsLoading(true);
      console.log({ params, reclaimProtocol, merchantBank }, "<--RECLAIM-->");
      const reclaimProofRequest = await ReclaimProofRequest.init(
        reclaimProtocol.APP_ID,
        reclaimProtocol.APP_SECRET,
        reclaimProtocol.PROVIDER_ID,
      );
      reclaimProofRequest.addContext(
        `redactedAccountNumber: ${redactedAccNum}`,
        `redactedTxnId: ${trans_id}`,
      );
      reclaimProofRequest.setParams(params);
      const requestUrl = await reclaimProofRequest.getRequestUrl();
      const statusUrl = await reclaimProofRequest.getStatusUrl();

      window.open(requestUrl, "_blank");

      console.log(requestUrl, "requestUrl <--RECLAIM-->");
      console.log(statusUrl, "statusUrl <--RECLAIM-->");

      const poolProof = setInterval(async () => {
        const data = await Axios.get(statusUrl);
        if (
          ["PROOF_GENERATION_SUCCESS", "PROOF_SUBMITTED"].includes(
            data?.data?.session?.statusV2,
          )
        ) {
          const proof = data.data.session.proofs?.[0];
          if (proof) {
            trackAmplitudeEvent("DISPUTE_PROOF_GENERATED");
            console.log(proof, "proof");
            const settleDisputeTransaction = await customPrepareContractCall({
              contract: orderProcessorContract,
              method: "settleDispute",
              params: [orderId, transformForOnchain(proof as any)],
              address: account?.address,
            });
            mutateSettleDispute([settleDisputeTransaction as any])
              .then(() => {
                trackAmplitudeEvent("DISPUTE_SETTLED_SUCCESSFULLY");
                successCallback();
                setIsLoading(false);
                toast({
                  title: "Dispute settle successfully.",
                });
              })
              .catch((err) => {
                trackAmplitudeEvent("DISPUTE_SETTLEMENT_FAILED");
                setIsLoading(false);
                console.error("Dispute settle failed", err);
                toast({
                  title: "Dispute settle failed.",
                  description: err.message,
                  variant: "destructive",
                });
              });
            clearInterval(poolProof);
          }
        }

        if (data?.data?.session?.statusV2 == "PROOF_GENERATION_FAILED") {
          trackAmplitudeEvent("DISPUTE_PROOF_GENERATION_FAILED");
          setIsLoading(false);
          console.error("Verification failed", data);
          toast({
            title: "Verification failed",
            variant: "destructive",
          });
          clearInterval(poolProof);
        }
      }, 5000);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      toast({
        description: "Something went wrong, try again.",
        variant: "destructive",
      });
    }
  };

  const openBankSelector = () => {
    trackAmplitudeEvent("DISPUTE_BANK_SELECTOR_OPENED");
    bankSelectorRef.current?.toggle();
  };

  return (
    <>
      {!merchantBank && (
        <>
          <Button
            label={"Set Bank to Proceed"}
            onClick={openBankSelector}
            loading={isLoading}
            type={ButtonType.primary}
            size={ButtonSize.small}
            widthFull
          />
          <BankSelector ref={bankSelectorRef} />
        </>
      )}

      {merchantBank && (
        <>
          {onlyCta ? (
            <Button
              label={label}
              onClick={() => openReclaimView(transId)}
              loading={isLoading}
              type={ButtonType.danger}
              size={ButtonSize.small}
              widthFull
            />
          ) : (
            <Bottomsheet
              title={"Set Trans Id"}
              description={"Type last four digit of Transaction Id"}
              includeActionButton
              buttonLabel={label}>
              <div className="px-4">
                <div className="mb-2">
                  <Input
                    label={"Last 4 digit"}
                    type={"number"}
                    name={"number"}
                    value={transIdValue ? transIdValue : ""}
                    onChange={(e) => {
                      if (e.target.value?.length > 4) return;
                      setTransIdValue(parseInt(e.target.value));
                    }}
                    placeholder={"XXXX"}
                  />
                </div>
                <Button
                  type={ButtonType.primary}
                  disabled={transIdValue?.toString()?.length !== 4}
                  label={"Confirm"}
                  loading={isPending || isLoading}
                  widthFull
                  onClick={handleSetDisputeTransId}
                />
              </div>
            </Bottomsheet>
          )}
        </>
      )}
    </>
  );
};

export default DisputeButton;
